import axios from "axios";

/**
 * Resolve the API base URL.
 *
 * In dev (preview / localhost) the build-time `REACT_APP_BACKEND_URL` points
 * at the same origin we're served from, so cookies are same-origin and Lax
 * works fine.
 *
 * In production (e.g. getequia.com) the bundle's `REACT_APP_BACKEND_URL` is
 * typically Emergent's internal deployment host (mobility-track-3.emergent.host),
 * which is a *different* domain from the custom domain the user is viewing.
 * That makes every API call cross-site → SameSite cookies are blocked → the
 * user appears logged out on the very next request.
 *
 * Fix: at runtime, if `window.location.origin` doesn't match the configured
 * backend's origin, route API calls through the *current* origin instead so
 * they stay same-origin (Emergent's ingress proxies /api on the custom domain
 * to the backend).
 */
function resolveApiBase() {
  const envUrl = process.env.REACT_APP_BACKEND_URL;
  if (typeof window === "undefined" || !envUrl) return envUrl || "";
  try {
    const envOrigin = new URL(envUrl).origin;
    if (envOrigin === window.location.origin) return envUrl;
    return window.location.origin;
  } catch {
    return envUrl;
  }
}

const API_URL = resolveApiBase();

const api = axios.create({
  baseURL: `${API_URL}/api`,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

// Response interceptor for auth
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const url = error.config?.url || "";
    const isAuthEndpoint = /\/auth\/(me|login|register|refresh|logout)/.test(url);
    if (error.response?.status === 401 && !error.config._retry && !isAuthEndpoint) {
      error.config._retry = true;
      try {
        await axios.post(`${API_URL}/api/auth/refresh`, {}, { withCredentials: true });
        return api(error.config);
      } catch {
        if (typeof window !== "undefined" && !window.location.pathname.startsWith("/login")) {
          window.location.href = "/login";
        }
      }
    }
    return Promise.reject(error);
  }
);

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e?.msg || JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export default api;
